/**
 * DockDragHandle — ⋮⋮ drag handle + click menu (4-kenar snap)
 * - Drag: 4 kenara sürükleyince snap
 * - Click: Pozisyon menüsü açılır
 */
import { GripVertical } from 'lucide-react'
import { useRef, useState } from 'react'
import type { DockPosition } from './useDockPosition'

interface DockDragHandleProps {
  currentPosition: DockPosition
  onDragStart?: () => void
  onDragEnd?: (newPosition: DockPosition) => void
}

const POSITIONS: { label: string; value: DockPosition; icon: string }[] = [
  { label: 'Bottom', value: 'bottom', icon: '⬇' },
  { label: 'Top',    value: 'top',    icon: '⬆' },
  { label: 'Left',   value: 'left',   icon: '⬅' },
  { label: 'Right',  value: 'right',  icon: '➡' },
]

export default function DockDragHandle({ currentPosition, onDragStart, onDragEnd }: DockDragHandleProps) {
  const [snapZone, setSnapZone] = useState<DockPosition | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const snapZoneRef = useRef<DockPosition | null>(null)
  const draggedRef = useRef(false)
  const startPosRef = useRef({ x: 0, y: 0 })

  const detectSnapZone = (x: number, y: number): DockPosition | null => {
    const vw = window.innerWidth
    const vh = window.innerHeight
    const threshold = 120
    if (x < threshold) return 'left'
    if (x > vw - threshold) return 'right'
    if (y < threshold) return 'top'
    if (y > vh - threshold) return 'bottom'
    return null
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    startPosRef.current = { x: e.clientX, y: e.clientY }
    draggedRef.current = false

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dx = Math.abs(moveEvent.clientX - startPosRef.current.x)
      const dy = Math.abs(moveEvent.clientY - startPosRef.current.y)
      // Drag treshold: 5px
      if (!draggedRef.current && (dx > 5 || dy > 5)) {
        draggedRef.current = true
        setIsDragging(true)
        onDragStart?.()
      }
      if (draggedRef.current) {
        const detected = detectSnapZone(moveEvent.clientX, moveEvent.clientY)
        snapZoneRef.current = detected
        setSnapZone(detected)
      }
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      if (draggedRef.current) {
        // Drag completed
        if (snapZoneRef.current) {
          onDragEnd?.(snapZoneRef.current)
        }
        setIsDragging(false)
        setSnapZone(null)
        snapZoneRef.current = null
      } else {
        // Just a click — toggle menu
        setShowMenu(prev => !prev)
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  return (
    <div style={{ position: 'relative' }}>
      <div
        onMouseDown={handleMouseDown}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          padding: '10px 12px',
          cursor: isDragging ? 'grabbing' : 'grab',
          opacity: isDragging ? 1 : (showMenu ? 1 : 0.5),
          transition: 'opacity 0.2s, background 0.15s, scale 0.2s',
          transform: isDragging ? 'scale(1.05)' : 'scale(1)',
          userSelect: 'none',
          borderRadius: 8,
          background: showMenu || isDragging ? 'var(--os-hover)' : 'rgba(99, 102, 241, 0.08)',
          border: isDragging ? '1px solid var(--os-accent)' : '1px solid rgba(99, 102, 241, 0.2)',
          minWidth: 42,
          minHeight: 34,
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.background = 'var(--os-hover)'
            e.currentTarget.style.opacity = '1'
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging && !showMenu) {
            e.currentTarget.style.background = 'rgba(99, 102, 241, 0.08)'
            e.currentTarget.style.opacity = '0.5'
          }
        }}
        title="⋮ Tıkla = Konum Menüsü | Sürükle = Dock Taşı"
      >
        <GripVertical size={18} color="var(--os-muted)" strokeWidth={1.5} />
      </div>

      {/* Drag overlay: snap zone preview */}
      {isDragging && (
        <>
          {/* Drop zone areas */}
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0,
            height: 100,
            background: snapZone === 'top' ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.05)',
            borderBottom: snapZone === 'top' ? '3px solid var(--os-accent)' : '1px solid rgba(99,102,241,0.2)',
            zIndex: 99998,
            pointerEvents: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s, border 0.15s',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-accent)', textTransform: 'uppercase', letterSpacing: '0.08em', opacity: snapZone === 'top' ? 1 : 0.5 }}>
              ⬆ Top
            </div>
          </div>

          <div style={{
            position: 'fixed',
            bottom: 0, left: 0, right: 0,
            height: 100,
            background: snapZone === 'bottom' ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.05)',
            borderTop: snapZone === 'bottom' ? '3px solid var(--os-accent)' : '1px solid rgba(99,102,241,0.2)',
            zIndex: 99998,
            pointerEvents: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s, border 0.15s',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-accent)', textTransform: 'uppercase', letterSpacing: '0.08em', opacity: snapZone === 'bottom' ? 1 : 0.5 }}>
              ⬇ Bottom
            </div>
          </div>

          <div style={{
            position: 'fixed',
            top: 0, left: 0, bottom: 0,
            width: 100,
            background: snapZone === 'left' ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.05)',
            borderRight: snapZone === 'left' ? '3px solid var(--os-accent)' : '1px solid rgba(99,102,241,0.2)',
            zIndex: 99998,
            pointerEvents: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s, border 0.15s',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-accent)', textTransform: 'uppercase', letterSpacing: '0.08em', opacity: snapZone === 'left' ? 1 : 0.5, writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
              ⬅ Left
            </div>
          </div>

          <div style={{
            position: 'fixed',
            top: 0, right: 0, bottom: 0,
            width: 100,
            background: snapZone === 'right' ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.05)',
            borderLeft: snapZone === 'right' ? '3px solid var(--os-accent)' : '1px solid rgba(99,102,241,0.2)',
            zIndex: 99998,
            pointerEvents: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s, border 0.15s',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--os-accent)', textTransform: 'uppercase', letterSpacing: '0.08em', opacity: snapZone === 'right' ? 1 : 0.5, writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
              Right ➡
            </div>
          </div>
        </>
      )}

      {/* Click menu */}
      {showMenu && !isDragging && (
        <>
          <div
            onClick={() => setShowMenu(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
          />
          <div style={{
            position: 'absolute',
            bottom: '100%',
            right: 0,
            marginBottom: 8,
            background: 'var(--os-surface)',
            backdropFilter: 'blur(20px)',
            border: '1px solid var(--os-border)',
            borderRadius: 10,
            padding: 6,
            minWidth: 140,
            boxShadow: 'var(--os-card-shadow)',
            zIndex: 9999,
          }}>
            <div style={{ fontSize: 9, color: 'var(--os-muted)', padding: '4px 8px 6px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Dock Position
            </div>
            {POSITIONS.map(p => {
              const active = currentPosition === p.value
              return (
                <button
                  key={p.value}
                  onClick={() => {
                    onDragEnd?.(p.value)
                    setShowMenu(false)
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%',
                    padding: '7px 10px',
                    background: active ? 'rgba(99,102,241,0.15)' : 'transparent',
                    border: 'none',
                    borderRadius: 6,
                    color: active ? 'var(--os-accent)' : 'var(--os-text)',
                    fontSize: 12,
                    fontWeight: active ? 600 : 400,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--os-hover)' }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent' }}
                >
                  <span>{p.icon}</span>
                  {p.label}
                  {active && <span style={{ marginLeft: 'auto', fontSize: 10 }}>✓</span>}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
